import React from 'react';
import { Button, Space } from 'antd';
import {
  useFetchAppUsageCount,
  useFetchNexaUsageCount,
  useFetchSSDUsageCount,
  useFetchVideoDimensionsUsageCount,
} from '@src/components/usage-dashboard/hooks/dataFetcherhooks';
import { intervalAtom } from '@src/components/usage-dashboard/atoms';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { nexaUnifiedSelector } from '@src/store/common/selectors';
import { checkCanViewNexaUsage } from '@src/globalStore/profileStore';
import { NEXA_USAGE_DASHBOARD_URL } from '@src/components/usage-dashboard/navigation';

const MonthlySummary = () => {
  const { count: appCountData, isFetching: isAppCountFetching }: any = useFetchAppUsageCount({
    enabled: true,
    product: 'APP',
  });

  const { count: SSDCountData, isFetching: isSSDCountFetching }: any = useFetchSSDUsageCount({
    enabled: true,
    product: 'EI',
  });

  const { count: videoDimensionsCountData, isFetching: isVideoDimensionsCountFetching }: any =
    useFetchVideoDimensionsUsageCount({
      enabled: true,
      product: 'EI',
    });

  const isNexaUnified = useSelector(nexaUnifiedSelector);
  const showNexaBlock = Boolean(isNexaUnified) && checkCanViewNexaUsage();

  const { count: nexaCountData, isFetching: isNexaCountFetching }: any = useFetchNexaUsageCount({
    enabled: showNexaBlock,
  });

  const [startDate, endDate] = useAtomValue(intervalAtom);
  const month = endDate.format('MMMM');

  const getTitleAndNumber = ({ title, value, loading, summaryEnd = false }) => {
    const borderRight = summaryEnd ? '' : 'solid 1px gray';
    return (
      <div style={{ padding: '0px 15px', borderRight }}>
        <div style={{ color: 'rgb(0,0,0,0.6)', fontWeight: '400' }}>{title}</div>
        <div style={{ fontWeight: 'bold' }}>{loading ? 'Loading' : value}</div>
      </div>
    );
  };

  const getProdct = (product) => {
    return (
      <div style={{ display: 'flex' }}>
        <div
          style={{ padding: '3px 10px', background: 'rgba(0, 0, 0, 0.09)', borderRadius: '6px' }}
        >
          {product}
        </div>
        <div style={{ padding: '3px 10px' }}>{`Monthly Usage: ${month}`}</div>
      </div>
    );
  };

  const getProductSummayForSSD = () => {
    return (
      <div style={{ padding: '5px', background: 'white', margin: '10px' }}>
        {getProdct('Video')}
        <div style={{ display: 'flex' }}>
          {getTitleAndNumber({
            title: 'Total Dimensions',
            value: videoDimensionsCountData?.totalDimensions,
            loading: isVideoDimensionsCountFetching,
          })}
          {getTitleAndNumber({
            title: 'Custom Dimensions',
            value: videoDimensionsCountData?.customDimensions,
            loading: isVideoDimensionsCountFetching,
          })}
          {getTitleAndNumber({
            title: 'SSD Daily',
            value: SSDCountData?.SSDDaily,
            loading: isSSDCountFetching,
          })}
          {getTitleAndNumber({
            title: 'SSD Hourly',
            value: SSDCountData?.SSDHourly,
            loading: isSSDCountFetching,
            summaryEnd: true,
          })}
        </div>
      </div>
    );
  };

  const getProductSummayForEco = () => {
    return (
      <div style={{ padding: '5px', background: 'white', margin: '10px' }}>
        {getProdct('App')}
        <div style={{ display: 'flex' }}>
          {getTitleAndNumber({
            title: 'Total Dimensions',
            value: appCountData?.totalDimensions,
            loading: isAppCountFetching,
          })}
          {getTitleAndNumber({
            title: 'Custom Dimensions',
            value: appCountData?.customDimensions,
            loading: isAppCountFetching,
          })}
          {getTitleAndNumber({
            title: 'Custom Metrics',
            value: appCountData?.metrics,
            loading: isAppCountFetching,
            summaryEnd: true,
          })}
        </div>
      </div>
    );
  };

  const openNexaUsageDashboard = () => {
    window.open(NEXA_USAGE_DASHBOARD_URL, '_blank');
  };

  const getProductSummaryForNexa = () => {
    return (
      <div style={{ padding: '5px', background: 'white', margin: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{ padding: '3px 10px', background: 'rgba(0, 0, 0, 0.09)', borderRadius: '6px' }}
          >
            NEXA
          </div>
          <div style={{ padding: '3px 10px' }}>{`Monthly Usage: ${month}`}</div>
          <Button type="link" size="small" onClick={openNexaUsageDashboard}>
            More Info
          </Button>
        </div>
        <div style={{ display: 'flex' }}>
          {getTitleAndNumber({
            title: 'Total Tokens',
            value: nexaCountData?.total_tokens,
            loading: isNexaCountFetching,
          })}
          {getTitleAndNumber({
            title: 'Total Requests',
            value: nexaCountData?.requests,
            loading: isNexaCountFetching,
          })}
          {getTitleAndNumber({
            title: 'Total Sessions',
            value: nexaCountData?.sessions,
            loading: isNexaCountFetching,
          })}
          {getTitleAndNumber({
            title: 'Active Users',
            value: nexaCountData?.active_users,
            loading: isNexaCountFetching,
            summaryEnd: true,
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex' }}>
      <Space>{getProductSummayForSSD()}</Space>
      <Space>{getProductSummayForEco()}</Space>
      {showNexaBlock && <Space>{getProductSummaryForNexa()}</Space>}
    </div>
  );
};

export default MonthlySummary;
